import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { api } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { 
  ArrowLeft, ArrowRight, Check, Building2, Coins, Settings, 
  FileText, AlertCircle, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

// Wizard steps
const STEPS = [
  { id: 'project', label: 'Select Project', icon: Building2 },
  { id: 'configure', label: 'Configure Security', icon: Settings },
  { id: 'review', label: 'Review & Create', icon: Check },
];

export default function InstrumentWizardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedProjectId = searchParams.get('project');
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Form state for security
  const [formData, setFormData] = useState({
    name: '',
    total_authorized_units: '',
    unit_price_at_issuance: '1',
    currency: 'EUR',
    distribution_rights: 'pro_rata',
    voting_rights: false,
    transferable: true,
    transfer_restrictions: '',
    redemption_terms: '',
  });

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/v2/projects');
      setProjects(res.data.projects || []);
      
      // If preselected project, find and select it
      if (preselectedProjectId) {
        const project = (res.data.projects || []).find(p => p.id === preselectedProjectId);
        if (project) {
          setSelectedProject(project);
          setFormData(prev => ({
            ...prev,
            name: `${project.title} - Common Units`,
          }));
          setCurrentStep(1); // Skip to configure step
        }
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [preselectedProjectId]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Step navigation
  const canProceed = () => {
    if (currentStep === 0) {
      return selectedProject !== null;
    }
    if (currentStep === 1) {
      return formData.name.trim() && 
             parseInt(formData.total_authorized_units) > 0 &&
             parseFloat(formData.unit_price_at_issuance) > 0;
    }
    return true;
  };

  const handleNext = () => {
    if (canProceed() && currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    if (!formData.name) {
      setFormData(prev => ({
        ...prev,
        name: `${project.title} - Common Units`,
      }));
    }
  };

  const handleCreateInstrument = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        project_id: selectedProject.id,
        name: formData.name,
        security_class: 'common',
        total_authorized_units: parseInt(formData.total_authorized_units),
        unit_price_at_issuance: formData.unit_price_at_issuance,
        currency: formData.currency,
        distribution_rights: formData.distribution_rights,
        voting_rights: formData.voting_rights,
        transferable: formData.transferable,
        transfer_restrictions: formData.transfer_restrictions || null,
        redemption_terms: formData.redemption_terms || null,
      };

      const res = await api.post('/admin/v2/captable/securities', payload);
      toast.success('Instrument created successfully!');
      
      // Navigate to the instruments list
      navigate(`/admin/instruments?created=${res.data.security?.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create instrument');
    } finally {
      setSaving(false);
    }
  };

  // Step 1: Select Project
  const renderProjectStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Select a Project</h3>
        <p className="text-sm text-foreground-muted">
          Choose the project to link this investment instrument to.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : projects.length === 0 ? (
        <Card className="p-8 text-center border-dashed">
          <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
          <h4 className="font-medium mb-2">No Projects Available</h4>
          <p className="text-sm text-foreground-muted mb-4">
            Create a project first before adding instruments.
          </p>
          <Button onClick={() => navigate('/admin/v2/projects/new')}>
            Create Project
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 max-h-[400px] overflow-y-auto pr-2">
          {projects.map((project) => (
            <Card
              key={project.id}
              className={cn(
                "p-4 cursor-pointer transition-all hover:border-primary/50",
                selectedProject?.id === project.id && "border-primary ring-2 ring-primary/20"
              )}
              onClick={() => handleSelectProject(project)}
              data-testid={`project-option-${project.id}`}
            >
              <div className="flex items-start gap-4">
                {project.image_urls?.[0] && (
                  <img
                    src={project.image_urls[0]}
                    alt=""
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium truncate">{project.title}</h4>
                    <Badge variant="outline" className="text-xs">
                      {project.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-foreground-muted truncate">
                    {project.primary_location?.city}, {project.primary_location?.country_code}
                  </p>
                  <p className="text-xs text-foreground-muted mt-1">
                    Code: {project.code}
                  </p>
                </div>
                {selectedProject?.id === project.id && (
                  <Check className="h-5 w-5 text-primary flex-shrink-0" />
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Step 2: Configure Security
  const renderConfigureStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Configure Security</h3>
        <p className="text-sm text-foreground-muted">
          Set up the investment units for {selectedProject?.title}.
        </p>
      </div>

      <div className="grid gap-6">
        {/* Security Name */}
        <div className="space-y-2">
          <Label htmlFor="name">Security Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Lisbon Apartment - Common Units"
            data-testid="security-name-input"
          />
        </div>

        {/* Units and Price */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="units">Total Authorized Units *</Label>
            <Input
              id="units"
              type="number"
              value={formData.total_authorized_units}
              onChange={(e) => setFormData({ ...formData, total_authorized_units: e.target.value })}
              placeholder="e.g., 100000"
              min="1"
              data-testid="units-input"
            />
            <p className="text-xs text-foreground-muted">
              Maximum units that can ever be issued
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="price">Unit Price at Issuance *</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted">€</span>
              <Input
                id="price"
                type="number"
                value={formData.unit_price_at_issuance}
                onChange={(e) => setFormData({ ...formData, unit_price_at_issuance: e.target.value })}
                placeholder="1.00"
                step="0.01"
                min="0.01"
                className="pl-8"
                data-testid="price-input"
              />
            </div>
          </div>
        </div>

        {/* Calculated Value */}
        {formData.total_authorized_units && formData.unit_price_at_issuance && (
          <Card className="p-4 bg-primary/5 border-primary/20">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Total Value at Issuance</span>
              <span className="text-lg font-bold text-primary">
                €{(parseInt(formData.total_authorized_units) * parseFloat(formData.unit_price_at_issuance)).toLocaleString()}
              </span>
            </div>
          </Card>
        )}

        {/* Currency */}
        <div className="space-y-2">
          <Label>Currency</Label>
          <Select
            value={formData.currency}
            onValueChange={(value) => setFormData({ ...formData, currency: value })}
          >
            <SelectTrigger data-testid="currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="EUR">EUR - Euro</SelectItem>
              <SelectItem value="USD">USD - US Dollar</SelectItem>
              <SelectItem value="GBP">GBP - British Pound</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Distribution Rights */}
        <div className="space-y-2">
          <Label>Distribution Rights</Label>
          <Select
            value={formData.distribution_rights}
            onValueChange={(value) => setFormData({ ...formData, distribution_rights: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pro_rata">Pro-Rata (equal per unit)</SelectItem>
              <SelectItem value="fixed">Fixed Return</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Toggles */}
        <div className="flex items-center justify-between py-3 border-t">
          <div>
            <Label>Transferable</Label>
            <p className="text-xs text-foreground-muted">Units can be transferred between investors</p>
          </div>
          <Switch
            checked={formData.transferable}
            onCheckedChange={(checked) => setFormData({ ...formData, transferable: checked })}
          />
        </div>

        <div className="flex items-center justify-between py-3 border-t">
          <div>
            <Label>Voting Rights</Label>
            <p className="text-xs text-foreground-muted">Unit holders can vote on project decisions</p>
          </div>
          <Switch
            checked={formData.voting_rights}
            onCheckedChange={(checked) => setFormData({ ...formData, voting_rights: checked })}
          />
        </div>

        {/* Optional Fields */}
        <div className="space-y-2">
          <Label htmlFor="restrictions">Transfer Restrictions (Optional)</Label>
          <Textarea
            id="restrictions"
            value={formData.transfer_restrictions}
            onChange={(e) => setFormData({ ...formData, transfer_restrictions: e.target.value })}
            placeholder="e.g., Subject to board approval, minimum holding period of 12 months"
            rows={2}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="redemption">Redemption Terms (Optional)</Label>
          <Textarea
            id="redemption"
            value={formData.redemption_terms}
            onChange={(e) => setFormData({ ...formData, redemption_terms: e.target.value })}
            placeholder="e.g., Redeemable at NAV after 24 months"
            rows={2}
          />
        </div>
      </div>
    </div>
  );

  // Step 3: Review
  const renderReviewStep = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Review & Create</h3>
        <p className="text-sm text-foreground-muted">
          Review the details before creating the instrument.
        </p>
      </div>

      <Card className="divide-y">
        {/* Project Info */}
        <div className="p-4">
          <h4 className="text-sm font-medium text-foreground-muted mb-2">Project</h4>
          <div className="flex items-center gap-3">
            {selectedProject?.image_urls?.[0] && (
              <img
                src={selectedProject.image_urls[0]}
                alt=""
                className="w-12 h-12 rounded-lg object-cover"
              />
            )}
            <div>
              <p className="font-medium">{selectedProject?.title}</p>
              <p className="text-sm text-foreground-muted">{selectedProject?.code}</p>
            </div>
          </div>
        </div>

        {/* Security Details */}
        <div className="p-4">
          <h4 className="text-sm font-medium text-foreground-muted mb-3">Security Details</h4>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-foreground-muted">Name</dt>
              <dd className="font-medium">{formData.name}</dd>
            </div>
            <div>
              <dt className="text-foreground-muted">Type</dt>
              <dd className="font-medium">Common Units</dd>
            </div>
            <div>
              <dt className="text-foreground-muted">Authorized Units</dt>
              <dd className="font-medium">{parseInt(formData.total_authorized_units).toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-foreground-muted">Unit Price</dt>
              <dd className="font-medium">€{parseFloat(formData.unit_price_at_issuance).toFixed(2)}</dd>
            </div>
            <div>
              <dt className="text-foreground-muted">Total Value</dt>
              <dd className="font-medium text-primary">
                €{(parseInt(formData.total_authorized_units) * parseFloat(formData.unit_price_at_issuance)).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt className="text-foreground-muted">Currency</dt>
              <dd className="font-medium">{formData.currency}</dd>
            </div>
          </dl>
        </div>

        {/* Terms */}
        <div className="p-4">
          <h4 className="text-sm font-medium text-foreground-muted mb-3">Terms</h4>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <dt className="text-foreground-muted">Distribution Rights</dt>
              <dd className="font-medium capitalize">{formData.distribution_rights.replace('_', ' ')}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-foreground-muted">Transferable</dt>
              <dd className="font-medium">{formData.transferable ? 'Yes' : 'No'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-foreground-muted">Voting Rights</dt>
              <dd className="font-medium">{formData.voting_rights ? 'Yes' : 'No'}</dd>
            </div>
          </dl>
        </div>
      </Card>

      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Important</p>
            <p className="text-sm text-amber-700 mt-1">
              Once created, authorized units and unit price cannot be changed. Open the offering on the instrument to start accepting subscriptions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]">
      
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/admin/instruments')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Instruments
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-heading font-semibold">Create Investment Instrument</h1>
          <p className="text-foreground-muted mt-1">
            Set up a new security to offer investment units to investors.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;
              
              return (
                <React.Fragment key={step.id}>
                  <div className="flex items-center gap-3">
                    <div
                      className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center transition-colors",
                        isCompleted && "bg-primary text-white",
                        isActive && "bg-primary/10 text-primary border-2 border-primary",
                        !isActive && !isCompleted && "bg-gray-100 text-gray-400"
                      )}
                    >
                      {isCompleted ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        <StepIcon className="h-5 w-5" />
                      )}
                    </div>
                    <span
                      className={cn(
                        "text-sm font-medium hidden sm:block",
                        isActive && "text-primary",
                        !isActive && "text-foreground-muted"
                      )}
                    >
                      {step.label}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <ChevronRight className="h-5 w-5 text-gray-300 mx-2" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <Card className="p-6" data-testid="wizard-content">
          {currentStep === 0 && renderProjectStep()}
          {currentStep === 1 && renderConfigureStep()}
          {currentStep === 2 && renderReviewStep()}
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          {currentStep < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              data-testid="next-step-btn"
            >
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleCreateInstrument}
              disabled={saving}
              data-testid="create-instrument-btn"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Create Instrument
                </>
              )}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
